import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Store as NgrxStore } from "@ngrx/store";
import { ResourceZoneStore } from "@app/services/store/resource-zone-store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@bryntum/core-thin";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ResourceZone } from "@app/models/rest/resource-zone";
import { ResourceModel } from "@app/models/rest/resource";
import { ZoneStore } from "@app/services/store/zone-store";
import { ResourceFactMappingPatternStore } from "@app/services/store/resource-fact-mapping-pattern-store";
import { HateoasResourceService } from "@lagoshny/ngx-hateoas-client";
import { Zone } from "@app/models/rest/zone";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";
import { ZoneSelectionDialogComponent } from "./zone-selection-dialog.component";
import { ConfirmZoneChangesDialogComponent } from "./confirm-zone-changes-dialog.component";
import {
  MatCard,
  MatCardContent,
  MatCardHeader,
  MatCardTitle
} from "@angular/material/card";
import { MatButton } from "@angular/material/button";
import { GridComponent } from "@app/view/shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";

@Component({
  selector: "app-resource-zone-mapping",
  templateUrl: "./resource-zone-mapping.component.html",
  styleUrls: ["./resource-zone-mapping.component.scss"],
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatButton,
    MatCardContent,
    GridComponent,
    MatPaginator
  ]
})
export class ResourceZoneMappingComponent
  extends BaseDataTablePageDirective<ResourceZone>
  implements OnInit
{
  resourceZoneStore: ResourceZoneStore;
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  private zoneStore = inject(ZoneStore);
  private resourceFactMappingPatternStore = inject(
    ResourceFactMappingPatternStore
  );
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private hateoasResourceService = inject(HateoasResourceService);
  private clientConfigService = inject(ClientConfigService);

  @Input() resource: ResourceModel;
  @Output() entityChange = new EventEmitter<any>();

  public zoneListStore = new Store({
    data: []
  });

  public mappingPatternListStore = new Store({
    data: []
  });

  @Input()
  public columns: object[] = [
    {
      text: "Zone",
      field: "zone",
      flex: 2,
      renderer: ({ record }) => {
        // Handle both direct objects and ProjectionRelType
        return record.zone
          ? typeof record.zone === "string"
            ? record.zone
            : record.zone.name
          : "none";
      },
      editable: false,
      editor: {
        type: "dropdown",
        store: this.zoneListStore,
        displayField: "name",
        valueField: null
      }
    },
    {
      field: "startDate",
      text: "Start Date",
      flex: 1,
      type: "date",
      format: "DD/MM/YYYY",
      editor: {
        type: "date",
        format: "DD/MM/YYYY"
      }
    },
    {
      field: "endDate",
      text: "End Date",
      flex: 1,
      type: "date",
      format: "DD/MM/YYYY",
      editor: {
        type: "date",
        format: "DD/MM/YYYY"
      }
    },
    {
      text: "Mapping Pattern",
      field: "mappingPattern",
      flex: 2,
      renderer: ({ record }) => {
        // Handle both direct objects and ProjectionRelType
        return record.mappingPattern
          ? typeof record.mappingPattern === "string"
            ? record.mappingPattern
            : record.mappingPattern.description
          : "none";
      },
      editor: {
        type: "dropdown",
        store: this.mappingPatternListStore,
        displayField: "description",
        valueField: null
      }
    }
  ];

  // Loading indicator for related entities
  public loadingRelatedEntities = false;

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const resourceZoneStore = inject(ResourceZoneStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      resourceZoneStore,
      store,
      dialog,
      "ResourceZone",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.resourceZoneStore = resourceZoneStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
    const hateoasResourceService = this.hateoasResourceService;
    const clientConfigService = this.clientConfigService;

    // Set the required services on the CustomAjaxStore class
    CustomAjaxStore.setClientConfigService(clientConfigService);
    CustomAjaxStore.setHateoasResourceService(hateoasResourceService);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  // Related entity fetching is no longer needed as we use the detail projection

  ngOnInit() {
    // Load zones for dropdown
    this.zoneStore.loadAll().then((value) => {
      this.zoneListStore.add(value.resources);
    });

    // Load mapping patterns for dropdown
    this.resourceFactMappingPatternStore.loadAll().then((value) => {
      // Add a "None" option to allow clearing the mapping
      const noneOption = {
        id: null,
        description: "None"
      };
      const filtered = value.resources.filter((r) => r.description !== "None");
      this.mappingPatternListStore.add([noneOption, ...filtered]);
    });

    super.ngOnInit();

    // Set autoCommit to false before loading data
    this.gridStore.autoCommit = false;

    // Set up filter for resource and detail projection
    if (this.resource) {
      this.gridStore.fixedFilters = [
        {
          property: "resource.id",
          operator: "Equals",
          value: this.resource.id
        }
      ];

      // Pass projection directly in the load parameters
      this.gridStore.load({
        params: {
          projection: "detail"
        }
      });
    }
  }

  addRow() {
    // Create a new ResourceZone with ProjectionRelType references
    const newResource = this.resource ? this.resource : null;

    // Open the zone selection dialog
    const dialogRef = this.dialog.open(ZoneSelectionDialogComponent, {
      data: {
        resource: newResource,
        zones: this.zoneListStore.records,
        mappingPatterns: this.mappingPatternListStore.records
      },
      width: "500px"
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.action === "save") {
        // Insert the new zone mapping from the dialog
        this.gridStore.insert(0, result.entry);
      }
    });
  }

  commit() {
    // Show confirmation dialog before saving changes
    const dialogRef = this.dialog.open(ConfirmZoneChangesDialogComponent, {
      width: "500px"
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        super.commit();
        this.entityChange.emit();
      }
    });
  }
}
